import { useEffect, useRef, useState, useCallback } from "react";
import axios from "axios";
import { Html5Qrcode } from "html5-qrcode";
import { QrCode, KeyRound, Search, CheckCircle2, XCircle, Clock, Copy, ArrowLeft } from "lucide-react";
import { beltHex, beltTextColor } from "../lib/beltColor";

const API = process.env.REACT_APP_BACKEND_URL;
const LS_CODE = "kiosk_code";

// Géoloc best-effort (pour la couche géofence facultative)
function getGeo() {
  return new Promise((resolve) => {
    if (!navigator.geolocation) return resolve(null);
    navigator.geolocation.getCurrentPosition(
      (pos) => resolve({ lat: pos.coords.latitude, lng: pos.coords.longitude }),
      () => resolve(null),
      { timeout: 4000, maximumAge: 60000 }
    );
  });
}

const RESULT_STYLE = {
  present: { bg: "bg-emerald-600", icon: CheckCircle2, label: "PRÉSENCE ENREGISTRÉE" },
  essai: { bg: "bg-cyan-600", icon: CheckCircle2, label: "COURS D'ESSAI" },
  present_retard: { bg: "bg-amber-500", icon: Clock, label: "RETARD NOTÉ" },
  doublon: { bg: "bg-blue-600", icon: CheckCircle2, label: "DÉJÀ ENREGISTRÉ" },
  refuse: { bg: "bg-red-600", icon: XCircle, label: "ACCÈS REFUSÉ" },
};

export default function KioskMode() {
  const [code, setCode] = useState(localStorage.getItem(LS_CODE) || "");
  const [info, setInfo] = useState(null);
  const [codeErr, setCodeErr] = useState("");
  const [tab, setTab] = useState("qr");
  const [pin, setPin] = useState("");
  const [query, setQuery] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [result, setResult] = useState(null); // {status, member_name, message,...}
  const [busy, setBusy] = useState(false);
  const scannerRef = useRef(null);
  const scanningRef = useRef(false);

  // ── Valider le code kiosque ──
  const tryConnect = useCallback(async (c) => {
    setCodeErr("");
    try {
      const { data } = await axios.get(`${API}/api/public/kiosk/info?code=${encodeURIComponent(c)}`);
      setInfo(data);
      localStorage.setItem(LS_CODE, c);
    } catch (e) {
      setCodeErr(e.response?.data?.detail || "Code invalide");
      setInfo(null);
    }
  }, []);

  useEffect(() => {
    if (code) tryConnect(code);
    // eslint-disable-next-line
  }, []);

  // ── Check-in commun ──
  const doCheckin = useCallback(async (body) => {
    if (busy) return;
    setBusy(true);
    try {
      const geo = await getGeo();
      const payload = { code, ...body };
      if (geo) { payload.lat = geo.lat; payload.lng = geo.lng; }
      const { data } = await axios.post(`${API}/api/public/kiosk/checkin`, payload);
      setResult(data);
    } catch (e) {
      setResult({ ok: false, status: "refuse", message: e.response?.data?.detail || "Erreur de check-in" });
    } finally {
      setBusy(false);
      setPin("");
      setQuery("");
      setSearchResults([]);
      setTimeout(() => setResult(null), 6000);
    }
  }, [busy, code]);

  // ── Scanner caméra (html5-qrcode) ──
  const startScanner = useCallback(async () => {
    if (scanningRef.current || !info) return;
    try {
      const scanner = new Html5Qrcode("kiosk-reader", { verbose: false });
      scannerRef.current = scanner;
      scanningRef.current = true;
      await scanner.start(
        { facingMode: "environment" },
        { fps: 10, qrbox: { width: 240, height: 240 } },
        async (decodedText) => {
          // pause pour éviter double scan
          try { await scanner.pause(true); } catch (_) {}
          await doCheckin({ qr_token: decodedText });
          setTimeout(() => { try { scanner.resume(); } catch (_) {} }, 4600);
        },
        () => {}
      );
    } catch (e) {
      scanningRef.current = false;
    }
  }, [info, doCheckin]);

  const stopScanner = useCallback(async () => {
    const s = scannerRef.current;
    if (s && scanningRef.current) {
      try { await s.stop(); } catch (_) {}
      try { await s.clear(); } catch (_) {}
    }
    scanningRef.current = false;
    scannerRef.current = null;
  }, []);

  useEffect(() => {
    if (info && tab === "qr") {
      const t = setTimeout(startScanner, 300);
      return () => { clearTimeout(t); stopScanner(); };
    } else {
      stopScanner();
    }
    // eslint-disable-next-line
  }, [info, tab]);

  // ── Recherche par nom ──
  useEffect(() => {
    if (tab !== "search" || query.trim().length < 2 || !info) { setSearchResults([]); return; }
    const t = setTimeout(async () => {
      try {
        const { data } = await axios.get(`${API}/api/public/kiosk/search?code=${encodeURIComponent(code)}&q=${encodeURIComponent(query)}`);
        setSearchResults(data.members || []);
      } catch (_) { setSearchResults([]); }
    }, 350);
    return () => clearTimeout(t);
  }, [query, tab, code, info]);

  // ════════ Écran de connexion du kiosque ════════
  if (!info) {
    return (
      <div className="min-h-screen bg-zinc-950 text-zinc-100 flex items-center justify-center p-6" data-testid="kiosk-login">
        <div className="w-full max-w-sm text-center">
          <div className="text-xs uppercase tracking-widest text-amber-500/80 mb-1">Institut Impact Élite Martial</div>
          <h1 className="text-2xl font-bold text-amber-300 mb-6">Mode Kiosque · Accueil</h1>
          <input
            data-testid="kiosk-code-input"
            value={code}
            onChange={(e) => setCode(e.target.value)}
            placeholder="Code du kiosque"
            className="w-full bg-zinc-900 border border-zinc-700 rounded-lg px-4 py-3 text-center text-lg tracking-widest mb-3 focus:border-amber-600 focus:outline-none"
          />
          {codeErr && <div className="text-red-400 text-sm mb-3" data-testid="kiosk-code-error">{codeErr}</div>}
          <button
            data-testid="kiosk-connect-btn"
            onClick={() => tryConnect(code)}
            className="w-full bg-amber-600 hover:bg-amber-500 text-zinc-950 font-bold py-3 rounded-lg transition-colors"
          >
            Activer la tablette
          </button>
          <p className="text-xs text-zinc-600 mt-4">Le code est défini dans Présences → Kiosque (admin).</p>
        </div>
      </div>
    );
  }

  const RS = result ? (RESULT_STYLE[result.status] || RESULT_STYLE.refuse) : null;
  const ResultIcon = RS?.icon;

  // ════════ Kiosque actif ════════
  return (
    <div className="min-h-screen bg-zinc-950 text-zinc-100 flex flex-col" data-testid="kiosk-active">
      {/* Overlay résultat */}
      {result && (
        <div className={`fixed inset-0 z-50 flex flex-col items-center justify-center ${RS.bg} text-white`} data-testid="kiosk-result">
          {result.member_name && (
            <div className="relative mb-4" data-testid="kiosk-result-photo">
              {result.photo_url ? (
                <img src={result.photo_url.startsWith("/") ? `${API}${result.photo_url}` : result.photo_url} alt={result.member_name}
                  className="w-56 h-56 rounded-full object-cover border-[6px] border-white/90 shadow-2xl" data-testid="kiosk-result-photo-img" />
              ) : (
                <div className="w-56 h-56 rounded-full bg-black/30 border-[6px] border-white/70 flex items-center justify-center text-8xl font-black" data-testid="kiosk-result-initial">
                  {result.member_name.charAt(0)}
                </div>
              )}
              {ResultIcon && <ResultIcon size={64} className="absolute -bottom-2 -right-2 bg-black/40 rounded-full p-2" />}
            </div>
          )}
          {!result.member_name && ResultIcon && <ResultIcon size={90} className="mb-4" />}
          <div className="text-sm uppercase tracking-widest opacity-90">{RS.label}</div>
          {result.member_name && <div className="text-5xl font-black mt-2 text-center px-4" data-testid="kiosk-result-name">{result.member_name}</div>}
          {result.belt && (
            <div className="mt-3 text-sm font-bold uppercase tracking-wide px-4 py-1.5 rounded-full border-2 border-white/40" data-testid="kiosk-result-belt"
              style={{ backgroundColor: beltHex(result.belt), color: beltTextColor(result.belt) }}>
              Ceinture {result.belt}
            </div>
          )}
          <div className="text-lg mt-3 opacity-95 text-center px-6">{result.message}</div>
          {result.member_name && !result.photo_url && (
            <div className="text-xs mt-4 opacity-80 text-center px-6">Pas encore de photo — ajoute-la depuis ta carte de membre 📷</div>
          )}
        </div>
      )}

      {/* Header */}
      <header className="px-6 py-4 border-b border-zinc-800 flex items-center justify-between">
        <div>
          <div className="text-xs uppercase tracking-widest text-amber-500/80">{info.dojo_name}</div>
          <div className="text-sm text-zinc-400">Présente-toi à l'accueil · {info.jour}</div>
        </div>
        <button
          data-testid="kiosk-exit"
          onClick={() => { stopScanner(); localStorage.removeItem(LS_CODE); setInfo(null); }}
          className="text-zinc-500 hover:text-zinc-300 flex items-center gap-1 text-xs"
        >
          <ArrowLeft size={14} /> Quitter
        </button>
      </header>

      {/* Tabs */}
      <div className="flex border-b border-zinc-800">
        {[
          ["qr", "Scanner QR", QrCode],
          ["pin", "Code PIN", KeyRound],
          ["search", "Mon nom", Search],
        ].map(([v, label, Icon]) => (
          <button
            key={v}
            data-testid={`kiosk-tab-${v}`}
            onClick={() => setTab(v)}
            className={`flex-1 py-4 flex flex-col items-center gap-1 text-sm font-medium transition-colors ${tab === v ? "bg-amber-600 text-zinc-950" : "text-zinc-400 hover:bg-zinc-900"}`}
          >
            <Icon size={22} /> {label}
          </button>
        ))}
      </div>

      <div className="flex-1 flex flex-col items-center justify-center p-6">
        {tab === "qr" && (
          <div className="w-full max-w-md text-center" data-testid="kiosk-qr-panel">
            <div id="kiosk-reader" className="mx-auto w-full max-w-sm rounded-xl overflow-hidden bg-black border border-zinc-800" style={{ minHeight: 280 }} />
            <p className="text-zinc-400 mt-4 text-lg">Présente ton QR code devant la caméra</p>
            {busy && <p className="text-amber-400 mt-1 animate-pulse">Traitement…</p>}
          </div>
        )}

        {tab === "pin" && (
          <div className="w-full max-w-xs text-center" data-testid="kiosk-pin-panel">
            <div className="text-3xl font-mono font-bold tracking-[0.4em] h-12 mb-4 text-amber-200">{pin.replace(/./g, "•")}</div>
            <div className="grid grid-cols-3 gap-3">
              {["1","2","3","4","5","6","7","8","9","C","0","OK"].map((k) => (
                <button
                  key={k}
                  data-testid={`kiosk-key-${k}`}
                  onClick={() => {
                    if (k === "C") setPin("");
                    else if (k === "OK") { if (pin.length >= 4) doCheckin({ pin }); }
                    else if (pin.length < 6) setPin(pin + k);
                  }}
                  className={`py-5 rounded-xl text-2xl font-bold transition-colors ${k === "OK" ? "bg-emerald-600 hover:bg-emerald-500 text-white" : k === "C" ? "bg-zinc-800 hover:bg-zinc-700 text-zinc-300" : "bg-zinc-900 hover:bg-zinc-800 border border-zinc-700"}`}
                  disabled={busy}
                >
                  {k}
                </button>
              ))}
            </div>
            <p className="text-zinc-500 mt-4 text-sm">Entre ton code PIN à 6 chiffres puis OK.</p>
          </div>
        )}

        {tab === "search" && (
          <div className="w-full max-w-md" data-testid="kiosk-search-panel">
            <input
              data-testid="kiosk-search-input"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Tape ton nom…"
              className="w-full bg-zinc-900 border border-zinc-700 rounded-lg px-4 py-4 text-xl mb-4 focus:border-amber-600 focus:outline-none"
              autoFocus
            />
            <div className="space-y-2 max-h-[50vh] overflow-y-auto">
              {searchResults.map((m) => (
                <button
                  key={m.contract_id}
                  data-testid={`kiosk-member-${m.contract_id}`}
                  onClick={() => doCheckin({ contract_id: m.contract_id })}
                  disabled={busy}
                  className="w-full flex items-center gap-3 bg-zinc-900 hover:bg-amber-900/40 border border-zinc-800 rounded-lg px-4 py-3 text-left transition-colors"
                >
                  <div className="w-10 h-10 rounded-full bg-amber-900/40 flex items-center justify-center font-bold text-amber-300">
                    {(m.full_name || "?").charAt(0)}
                  </div>
                  <div>
                    <div className="font-semibold">{m.full_name}</div>
                    <div className="text-xs text-zinc-500 capitalize">{(m.discipline || "").replace("_", " ")}</div>
                  </div>
                </button>
              ))}
              {query.length >= 2 && searchResults.length === 0 && (
                <div className="text-center text-zinc-500 py-6">Aucun membre trouvé. Voir à l'accueil.</div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
